import { ChartDepths, ChartLayersBase } from './mg-layers';
import Layer from 'ol/layer/Layer';
import TileLayer from 'ol/layer/WebGLTile';
import XYZ from 'ol/source/XYZ';
import { createXYZ } from 'ol/tilegrid';
import { StyleName } from '../../local_modules/voyage-style/src/voyage-style';
import { Tile, ImageTile } from 'ol';
import {Options} from 'ol/layer/BaseTile'
import { Source } from 'ol/source';
import { AuthService } from '../services/auth-service';
import TileState from 'ol/TileState';
import { getTokenInfo } from '../services/auth-config';
import { Vector } from 'ol/layer';
import { Vector as SourceVector } from 'ol/source';
import {GeoJSON} from 'ol/format'
import { Fill, Stroke, Style, Text } from 'ol/style';

// EPSG:4326 - Unprojected
// EPSG:3395 - World Mercator
// EPSG:3857 - Pseudo/Web Mercator

const _PROJECTION = (<any>window)["appenv"].projection;
const _ENCTILESERVER = (<any>window)["appenv"].services.encTileServer;
const _MAPTILESERVER = (<any>window)["appenv"].services.mapTileServer;

const isVessel = (<any>window)["appenv"].vesselIMONumber !== undefined;

export class WBChartLayersImpl extends ChartLayersBase
{
    constructor() {
        super();
    }
    get layers() {
        return this._layers;
    }
    setChartDepths(depths: ChartDepths) {
        this._depths = depths;
        this._layers.forEach(layer => {
            const source = layer.getSource();
            //Refreshes the source. The source will be cleared, and data from the server will be reloaded.
            source!.refresh();
        })
    }
    setColorScheme(colorScheme: StyleName) {
        switch (colorScheme)
        {
            case StyleName.dusk:
                this._colorScheme = 'dusk';
                break;

            case StyleName.night:
                this._colorScheme = 'night';
                break;
            
            default:
                this._colorScheme = 'day';
                break;
        }

        localStorage.setItem('voyage-style', this._colorScheme);

        for (let layer of this._layers) {
            let src =layer.getSource()!;
            //At the time of writing this code, refresh() does not clear tile cache,
            // so changing theme doesnt take effect even after running refresh.
            // Clearing the tile cache manually is a workaround:
            // https://github.com/openlayers/openlayers/issues/13051
            //@ts-ignore
            layer.getRenderer().tileTextureCache_?.clear();
            src.changed();
        }
    }

    async initialize(mapConfig: any) {
        let savedColorScheme:(string | null) = localStorage.getItem('voyage-style') || StyleName.default;
        
        if (!savedColorScheme || savedColorScheme === "default") {
            savedColorScheme = StyleName.default;
        }
        
        this._colorScheme = savedColorScheme;
        
        const authLoaderForScope = (scope: string) => {
            return async (tile: Tile, url: string) => {
                const imageElement = (<ImageTile>tile).getImage() as HTMLImageElement;

                let parsedUrl = url + `?safetyContour=${this._depths.safetyContour}&safetyDepth=${this._depths.safetyDepth}&shallowContour=${this._depths.shallowContour}&deepContour=${this._depths.deepContour}&beauty=true&simplified=${mapConfig.simplified}&levelOfDetail=${mapConfig.level}&coverage=false&palette=${this._colorScheme}`;
                /*START.DEV_ONLY*/
                parsedUrl = url + `?safetyContour=${this._depths.safetyContour}&safetyDepth=${this._depths.safetyDepth}&shallowContour=${this._depths.shallowContour}&deepContour=${this._depths.deepContour}&beauty=true&simplified=${mapConfig.simplified}&levelOfDetail=${mapConfig.level}&coverage=false&palette=${this._colorScheme}`;
                /*END.DEV_ONLY*/
                let accessToken;
                if ((<any>window)["appenv"].authRequired) accessToken = getTokenInfo();

                try {
                    let response = await fetch(parsedUrl, {
                        mode: 'cors',
                        method: 'GET',
                        headers: accessToken ? {
                            'Authorization': `Bearer ${accessToken}`
                        } : {}
                    });
    
                    const objectURL = URL.createObjectURL(await response.blob());
                    imageElement.src = objectURL;
                } catch (err) {
                    tile.setState(TileState.ERROR);
                }
            }
        }

        const extent = <[number,number,number,number]>[-20037508.342789244, -20037508.342789244, 20037508.342789244, 20037508.342789244];
        const tileSize = [256, 256];
        const mapTileGrid1 = createXYZ({
            extent,
            tileSize,
	        maxZoom: 4,
        });
        const mapTileGrid2 = createXYZ({
            extent,
            tileSize,
	        minZoom: 5,
        });
        const encTileGrid = createXYZ({
            extent,
            tileSize,
        });

        const tilePixelRatio = 1;

        var vector = new Vector({
            source: new SourceVector({
                format: new GeoJSON(),
                url: "./countries.geojson"
            }),
             style: function(feature, res){
                return new Style({
                    stroke: new Stroke({
                        color: 'rgba(0, 0, 0, 1.0)',
                        width: 1
                    }),
                    text: new Text({
                        text: feature.get('name'),
                        font: '12px Calibri,sans-serif',
                        overflow: true,
                        fill: new Fill({
                            color: '#000'
                        }),
                        stroke: new Stroke({
                            color: '#fff',
                            width: 3
                        })
                    })
                });
             }
        });

        vector.set('name', 'flag_vectorStroke');
        
        this._layers.push(
            new TileLayer(<Options<XYZ>>{
                preload: 4,
                source: new XYZ({
                    url: `${_MAPTILESERVER.baseurl}/map/{z}/{x}/{-y}.png`,
                    tileLoadFunction: authLoaderForScope(_MAPTILESERVER.scope),
                    tileGrid: mapTileGrid1,
                    tilePixelRatio,
                    projection: _PROJECTION,
                }),
            }),
            new TileLayer(<Options<XYZ>>{
                minZoom: 4,
                source: new XYZ({
                    url: `${_MAPTILESERVER.baseurl}/map/{z}/{x}/{-y}.png`,
                    tileLoadFunction: authLoaderForScope(_MAPTILESERVER.scope),
                    tileGrid: mapTileGrid2,
                    tilePixelRatio,
                    projection: _PROJECTION,
                }),
            }),
            new TileLayer(<Options<XYZ>>{
                source: new XYZ({
                    url: `${_ENCTILESERVER.baseurl}/enc/{z}/{x}/{-y}.png`,
                    tileLoadFunction: authLoaderForScope(_MAPTILESERVER.scope),
                    tileGrid: encTileGrid,
                    tilePixelRatio,
                    projection: _PROJECTION,
                }),
            }),
            vector
        );
    
        this._initialized = true;
    }

    zoomLevelChanged(zoom: number) {
    }


    private _initialized: boolean = false;
    private _layers: Layer<Source>[] = [];
    private _colorScheme: string = 'day';
    private _depths: ChartDepths = {
        safetyContour: 20,
        shallowContour: 15,
        deepContour: 30,
        safetyDepth: 20
    };
}
